import type { Node, NodeChange } from '@xyflow/react'
import {
  commitCornerResize,
  cssLength,
  initialFlowStyle,
  resolveNodeBox,
  type NodeSizeSource,
} from '@genieartinc/schemas'

function nodeState(node: Node): Record<string, unknown> {
  const data = node.data as Record<string, unknown> | undefined
  const state = data?.state
  return state && typeof state === 'object' ? state as Record<string, unknown> : {}
}

/** Stamp a finished corner drag. Flow units, not screen pixels. */
export function stampManualSize(node: Node, width: number, height: number, zoom = 1): Node {
  const box = commitCornerResize(width, height, zoom)
  const data = (node.data ?? {}) as Record<string, unknown>
  return {
    ...node,
    width: box.width,
    height: box.height,
    style: { ...(node.style ?? {}), width: box.width, height: box.height },
    data: {
      ...data,
      state: { ...nodeState(node), sizeSource: 'manual' satisfies NodeSizeSource },
    },
  }
}

function isResizeEnd(change: NodeChange): change is NodeChange & {
  type: 'dimensions'
  id: string
  resizing: false
  dimensions: { width: number; height: number }
} {
  return change.type === 'dimensions' && change.resizing === false && !!change.dimensions
}

/**
 * User corner drags end with `resizing: false`. Measurements omit that flag
 * and must not become a manual size.
 */
export function commitResizeChanges(nodes: Node[], changes: NodeChange[]): { nodes: Node[]; committed: boolean } {
  const ended = changes.filter(isResizeEnd)
  if (ended.length === 0) return { nodes, committed: false }
  const byId = new Map(ended.map((change) => [change.id, change.dimensions]))
  return {
    committed: true,
    nodes: nodes.map((node) => {
      const dims = byId.get(node.id)
      return dims ? stampManualSize(node, dims.width, dims.height) : node
    }),
  }
}

/** Persist a real edit. Measurement-only dimension events are not one. */
export function shouldPersistNodeChanges(changes: NodeChange[], committedResize: boolean): boolean {
  if (committedResize) return true
  return changes.some((change) => change.type !== 'dimensions' && change.type !== 'select')
}

/**
 * Reopen: an explicit style width and height is kept, including a non-square
 * box from an older canvas. A width with no style height (the old wide
 * rectangle, whose short side was only a measurement) becomes square.
 * The saved width is not replaced. Top-level `height` alone is not a manual size.
 */
export function hydrateSavedNodeBox(node: Node): Node {
  const style = node.style ?? {}
  const styleWidth = cssLength(style.width)
  const styleHeight = cssLength(style.height)
  if (styleWidth && styleHeight) return node
  const width = styleWidth ?? cssLength(node.width)
  const box = resolveNodeBox({
    componentType: node.type,
    savedWidth: width,
    savedHeight: undefined,
    sizeSource: 'default',
  })
  if (styleWidth === box.width && styleHeight === box.height) return node
  return {
    ...node,
    width: box.width,
    height: box.height,
    style: { ...style, width: box.width, height: box.height },
  }
}

export function flowNodeStyle(
  componentType: string | undefined,
  dimensions?: { width?: number; height?: number },
): { width: number; height: number } {
  return initialFlowStyle(componentType, dimensions)
}
